import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	LoadingOutlined,
} from "@ant-design/icons";
import { useConfirmAccountDeletion } from "@bodhistreams/queries/src";
import { theme } from "antd";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageLayout from "../PageLayout";
import styles from "./DeleteAccount.module.css";

/**
 * Landing page opened by the "Delete my account permanently" link in the
 * deletion-confirmation email. Confirms the deletion with the single-use
 * token from the URL, then shows the outcome.
 */
const ConfirmDeletion = () => {
	const { token } = theme.useToken();
	const { token: deletionToken } = useParams();
	const confirmDeletionMutation = useConfirmAccountDeletion();
	const [state, setState] = useState("confirming");
	const [resultMessage, setResultMessage] = useState(null);

	useEffect(() => {
		if (!deletionToken) {
			setState("error");
			setResultMessage(
				"This link is missing its confirmation token. Request a new deletion email from the app.",
			);
			return;
		}
		confirmDeletionMutation
			.mutateAsync(deletionToken)
			.then((response) => {
				setState("success");
				setResultMessage(
					response.message ||
						"Your account and data have been permanently deleted.",
				);
			})
			.catch((error) => {
				setState("error");
				setResultMessage(
					error.response?.data?.error ||
						"This deletion link is invalid, already used, or expired. Request a new deletion email from the app.",
				);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deletionToken]);

	return (
		<PageLayout>
			<div className={styles.page} style={{ "--da-text": token.colorText }}>
				<header className={styles.hero}>
					<p className={styles.eyebrow}>Account</p>
					<h1 className={styles.title}>Delete Your Account</h1>
				</header>

				<div
					className={styles.callout}
					style={{
						borderColor:
							state === "success"
								? token.colorSuccessBorder
								: state === "error"
									? token.colorErrorBorder
									: token.colorBorderSecondary,
						background:
							state === "success"
								? token.colorSuccessBg
								: state === "error"
									? token.colorErrorBg
									: undefined,
					}}
				>
					{state === "confirming" && (
						<>
							<LoadingOutlined
								style={{ fontSize: 20, color: token.colorPrimary }}
							/>
							<p>
								<strong>Confirming deletion…</strong> Your account is being
								permanently removed. Please don&rsquo;t close this page.
							</p>
						</>
					)}
					{state === "success" && (
						<>
							<CheckCircleOutlined
								style={{ fontSize: 20, color: token.colorSuccess }}
							/>
							<p>
								<strong>Account deleted.</strong> {resultMessage} We&rsquo;re
								sorry to see you go — you&rsquo;re always welcome back.
							</p>
						</>
					)}
					{state === "error" && (
						<>
							<CloseCircleOutlined
								style={{ fontSize: 20, color: token.colorError }}
							/>
							<p>
								<strong>Deletion not completed.</strong> {resultMessage}
							</p>
						</>
					)}
				</div>

				<p className={styles.footer}>
					Back to{" "}
					<Link to="/" className={styles.link}>
						BodhiStreams
					</Link>{" "}
					· Contact us at{" "}
					<a href="mailto:support@bodhistreams.com" className={styles.link}>
						support@bodhistreams.com
					</a>
				</p>
			</div>
		</PageLayout>
	);
};

export default ConfirmDeletion;
